import { useAppDispatch, useAppSelector } from "hooks/redux";
import { cloneDeep } from "lodash";
import { useWebsiteBuilderDebouncedUpdate, WebsiteBuilderDebouncedUpdateTime } from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { IWebsiteBuilderFlexSettings, TWebsiteBuilderFlexAlignItems, TWebsiteBuilderFlexDirection, TWebsiteBuilderFlexJustifyContent, WEBSITE_BUILDER_FLEX_MAX_GAP, WEBSITE_BUILDER_FLEX_MIN_GAP } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderFlex";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { WebsiteBuilderNumberInput } from "pages/secure/facility/websiteBuilder/editor/components/numberInput/WebsiteBuilderNumberInput";
import { websiteBuilderUpdateContainerSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { Select } from "components/select/index";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { IWebsiteBuilderSelectOption, renderWebsiteBuilderSelectOptions } from "../../../../../helpers/renderWebsiteBuilderSelectOptions";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";

interface IFlexSettingsProps {
  id: string;
  settings: IWebsiteBuilderFlexSettings;
  closeSettings: () => void;
}

export function FlexSettings(props: IFlexSettingsProps) {
  const { id, settings, closeSettings } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderFlexSettings>(() => cloneDeep(settings));

  const directionOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderFlexDirection>[] = useMemo(() => {
    return [
      {
        label: t("Horizontal"),
        value: "row",
      },
      {
        label: t("Vertical"),
        value: "column",
      },
    ];
  }, []);

  const justifyContentOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderFlexJustifyContent>[] = useMemo(() => {
    return [
      {
        label: t("Start"),
        value: "flex-start",
      },
      {
        label: t("Center"),
        value: "center",
      },
      {
        label: t("End"),
        value: "flex-end",
      },
      {
        label: t("Space Between"),
        value: "space-between",
      },
    ];
  }, []);

  const alignItemsOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderFlexAlignItems>[] = useMemo(() => {
    return [
      {
        label: t("Start"),
        value: "flex-start",
      },
      {
        label: t("Center"),
        value: "center",
      },
      {
        label: t("End"),
        value: "flex-end",
      },
    ];
  }, []);

  function dispatchSettings(prevSettings: IWebsiteBuilderFlexSettings, nextSettings: IWebsiteBuilderFlexSettings) {
    dispatch(
      websiteBuilderUpdateContainerSettings(activePageId, deviceView, id, {
        id,
        prevContainerSettings: {
          type: "flex",
          settings: cloneDeep(prevSettings),
        },
        nextContainerSettings: {
          type: "flex",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function renderSettings() {
    return (
      <>
        <Select
          label={t("Direction")}
          defaultValue={updatedSettings.direction}
          onChange={(direction: TWebsiteBuilderFlexDirection) =>
            handleUpdateState(prev => ({
              ...prev,
              direction,
            }))
          }
          usePortal
          portalId={WEBSITE_BUILDER_PORTAL}
        >
          {renderWebsiteBuilderSelectOptions(directionOptions)}
        </Select>
        <Select
          label={t("Justify")}
          defaultValue={updatedSettings.justifyContent}
          onChange={(justifyContent: TWebsiteBuilderFlexJustifyContent) =>
            handleUpdateState(prev => ({
              ...prev,
              justifyContent,
            }))
          }
          usePortal
          portalId={WEBSITE_BUILDER_PORTAL}
        >
          {renderWebsiteBuilderSelectOptions(justifyContentOptions)}
        </Select>
        <Select
          label={t("Align")}
          defaultValue={updatedSettings.alignItems}
          onChange={(alignItems: TWebsiteBuilderFlexAlignItems) =>
            handleUpdateState(prev => ({
              ...prev,
              alignItems,
            }))
          }
          usePortal
          portalId={WEBSITE_BUILDER_PORTAL}
        >
          {renderWebsiteBuilderSelectOptions(alignItemsOptions)}
        </Select>
        <WebsiteBuilderNumberInput
          label={t("Gap")}
          initialValue={updatedSettings.gap}
          min={WEBSITE_BUILDER_FLEX_MIN_GAP}
          max={WEBSITE_BUILDER_FLEX_MAX_GAP}
          required
          onChange={gap => {
            handleDebouncedUpdateState(prev => ({
              ...prev,
              gap,
            }));
          }}
        />
      </>
    );
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("Stack style")}
      closePanel={closeSettings}
      sections={[renderSettings()]}
    />
  );
}